import { supabase } from '@/integrations/supabase/client';

/**
 * Event types for analytics tracking
 */
export type AnalyticsEventType = 
  | 'calculator_input'
  | 'feature_used'
  | 'conversation'
  | 'report_generated'
  | 'page_view'
  | 'export'
  | 'share'
  | 'error';

/**
 * Event categories for organizing analytics
 */
export type AnalyticsEventCategory = 
  | 'calculator'
  | 'chatbot'
  | 'inventory'
  | 'report'
  | 'navigation'
  | 'system';

/**
 * Event actions
 */
export type AnalyticsEventAction = 
  | 'change'
  | 'create'
  | 'view'
  | 'export'
  | 'update'
  | 'delete'
  | 'complete'
  | 'error';

/**
 * Analytics event structure
 */
export interface AnalyticsEvent {
  eventType: AnalyticsEventType;
  eventCategory: AnalyticsEventCategory;
  eventAction: AnalyticsEventAction;
  eventData?: Record<string, any>;
  pagePath?: string;
}

/**
 * Service for tracking and persisting user analytics events
 */
class AnalyticsTrackingService {
  private eventQueue: AnalyticsEvent[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private sessionId: string;
  private readonly FLUSH_INTERVAL_MS = 5000; // Flush every 5 seconds
  private readonly MAX_QUEUE_SIZE = 50; // Flush when queue reaches this size

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startAutoFlush();
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Start automatic flushing of events
   */
  private startAutoFlush(): void {
    this.flushInterval = setInterval(() => {
      this.flushEvents();
    }, this.FLUSH_INTERVAL_MS);
  }

  /**
   * Stop automatic flushing
   */
  public stopAutoFlush(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }

  /**
   * Get current page path
   */
  private getCurrentPagePath(): string {
    return window.location.pathname;
  }

  /**
   * Track a generic event
   */
  public async trackEvent(
    eventType: AnalyticsEventType,
    eventCategory: AnalyticsEventCategory,
    eventAction: AnalyticsEventAction,
    eventData?: Record<string, any>
  ): Promise<void> {
    const event: AnalyticsEvent = {
      eventType,
      eventCategory,
      eventAction,
      eventData,
      pagePath: this.getCurrentPagePath()
    };

    this.eventQueue.push(event);

    // Flush if queue is full
    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      await this.flushEvents();
    }
  }

  /**
   * Track calculator input changes
   */
  public async trackCalculatorInput(
    field: string,
    value: any,
    calculatorType: 'mortgage' | 'buying_power' | 'affordability' = 'mortgage'
  ): Promise<void> {
    await this.trackEvent('calculator_input', 'calculator', 'change', {
      field,
      value,
      calculatorType,
      timestamp: Date.now()
    });
  }

  /**
   * Track feature usage
   */
  public async trackFeatureUsage(
    featureName: string,
    action: AnalyticsEventAction = 'view'
  ): Promise<void> {
    await this.trackEvent('feature_used', 'navigation', action, {
      featureName,
      timestamp: Date.now()
    });
  }

  /**
   * Track conversation events
   */
  public async trackConversation(
    conversationId: string,
    action: AnalyticsEventAction,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('conversation', 'chatbot', action, {
      conversationId,
      ...metadata,
      timestamp: Date.now()
    });
  }

  /**
   * Track report generation
   */
  public async trackReportGeneration(
    reportType: string,
    data?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('report_generated', 'report', 'create', {
      reportType,
      ...data,
      timestamp: Date.now()
    });
  }

  /**
   * Track page views
   */
  public async trackPageView(pagePath?: string): Promise<void> {
    await this.trackEvent('page_view', 'navigation', 'view', {
      pagePath: pagePath || this.getCurrentPagePath(),
      timestamp: Date.now(),
      referrer: document.referrer
    });
  }

  /**
   * Track export actions
   */
  public async trackExport(
    exportType: string,
    format: string,
    data?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('export', 'report', 'export', {
      exportType,
      format,
      ...data,
      timestamp: Date.now()
    });
  }

  /**
   * Track errors
   */
  public async trackError(
    errorMessage: string,
    errorContext?: Record<string, any>
  ): Promise<void> {
    await this.trackEvent('error', 'system', 'error', {
      errorMessage,
      ...errorContext,
      timestamp: Date.now()
    });
  }

  /**
   * Flush all queued events to the database
   */
  public async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    const eventsToFlush = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const eventsWithMetadata = eventsToFlush.map(event => ({
        user_id: user?.id || null,
        session_id: this.sessionId,
        event_type: event.eventType,
        event_category: event.eventCategory,
        event_action: event.eventAction,
        event_data: event.eventData || {},
        page_path: event.pagePath || this.getCurrentPagePath(),
        created_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('user_events')
        .insert(eventsWithMetadata);

      if (error) {
        console.error('Failed to persist analytics events:', error);
        // Re-queue failed events
        this.eventQueue.unshift(...eventsToFlush);
      }
    } catch (error) {
      console.error('Error flushing analytics events:', error);
      // Re-queue failed events
      this.eventQueue.unshift(...eventsToFlush);
    }
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Reset session (useful for new sessions)
   */
  public resetSession(): void {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopAutoFlush();
    this.flushEvents(); // Final flush before cleanup
  }
}

// Export singleton instance
export const analyticsTrackingService = new AnalyticsTrackingService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    analyticsTrackingService.flushEvents();
  });
}
